import { Link } from "@tanstack/react-router";
import { Boxes, Store, ShoppingBag, Building2, Sparkles } from "lucide-react";
import { LandingShell, Accent } from "./LandingShell";

/**
 * AMCaaS (B2C) marketing landing — shown at `/` on the amcaas.app host to signed-out
 * visitors. Two doors: Provider console + Platform (marketplace). App-branded violet
 * (WL-04). Self-registration is the secondary CTA under the doors.
 */
const ACCENT = "#334155"; // slate-700 — greyish brand accent
const ACCENT2 = "#64748b"; // slate-500 — gradient companion

export function AmcaasLandingPage(): JSX.Element {
  return (
    <LandingShell
      appName="AMCaaS"
      appTagline="Annual Maintenance Contracts as a Service"
      logoSrc="/logos/amcaas-login-banner.png"
      logoSrc2x="/logos/amcaas-login-banner@2x.png"
      accent={ACCENT}
      accent2={ACCENT2}
      badge="✨ Discover services from every provider"
      headline={
        <>
          Discover and subscribe to{" "}
          <Accent from={ACCENT} to={ACCENT2}>
            services you love
          </Accent>
          .
        </>
      }
      blurb="Browse the marketplace, subscribe in minutes, and manage everything you use across providers from a single account."
      stats={[
        { value: "500+", label: "Services" },
        { value: "120+", label: "Providers" },
        { value: "10k+", label: "Subscribers" },
        { value: "4.9/5", label: "Rating" },
      ]}
      features={[
        {
          icon: Boxes,
          title: "One marketplace",
          blurb: "Every provider's services, searchable in one place.",
        },
        {
          icon: ShoppingBag,
          title: "Subscribe in minutes",
          blurb: "Self-service checkout — no sales calls.",
        },
        {
          icon: Sparkles,
          title: "One account",
          blurb: "Manage subscriptions across providers together.",
        },
      ]}
      doors={[
        {
          href: "/login/platform",
          eyebrow: "Platform",
          title: "Platform sign-in",
          blurb: "Browse the marketplace and manage your subscriptions.",
          bullets: [
            "Browse marketplace",
            "Subscribe in minutes",
            "Manage everything",
          ],
          icon: Store,
          primary: true,
        },
        {
          href: "/login/provider",
          eyebrow: "Provider",
          title: "Provider sign-in",
          blurb: "Publish to the marketplace and manage your subscribers.",
          bullets: ["Publish services", "Win subscribers", "Billing & clients"],
          icon: Building2,
        },
      ]}
      footerCta={
        <>
          New to AMCaaS?{" "}
          <Link
            to="/register"
            className="font-medium text-foreground hover:underline"
          >
            Create an account
          </Link>
        </>
      }
    />
  );
}
